import { useState, useEffect, useCallback } from 'react';
import { isApiError } from '@aiptyg/types';
import * as settingsService from '../services/welfare-popup.service';
import type { WelfarePopupConfigDTO, WelfarePopupUpdateParams } from '../types/welfare-popup.types';
import { useToastStore } from '../../../store/toast.store';

function resolveError(e: unknown): string {
  if (isApiError(e)) {
    const map: Record<string, string> = {
      admin_role_required: '无管理员权限',
      'invalid welfare popup': '请检查内容块；启用时至少填写文字、图片或富媒体，音视频须为 HTTPS 直链',
      network_error: '网络连接失败，请检查网络',
    };
    return map[e.message] ?? `请求失败 (${e.message})`;
  }
  return '未知错误';
}

export function useWelfarePopup() {
  const addToast = useToastStore((s) => s.addToast);
  const [data, setData] = useState<WelfarePopupConfigDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await settingsService.getWelfarePopup());
    } catch (e) {
      setError(resolveError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const update = useCallback(async (params: WelfarePopupUpdateParams) => {
    setSaving(true);
    try {
      const result = await settingsService.updateWelfarePopup(params);
      setData(result);
      addToast('success', '福利活动弹窗已更新');
      return true;
    } catch (e) {
      addToast('error', resolveError(e));
      return false;
    } finally {
      setSaving(false);
    }
  }, [addToast]);

  return { data, loading, saving, error, reload: load, update };
}
